import { Button, Stack } from "@mui/material";

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { StyledCircularProgress } from "../../../components/Loading/styles";
import { PdfViewer } from "../../../components/PDFViewer/PDFViewer";
import useMailingOnBoardingService from "../../../shared/services/useMailingOnBoardingService";
import ContainerContent from "../components/ContainerContent";
import { EMailingStepsEnum, MailingStepProps } from "../index.types";
import { needsSecondDocument, getContractDataFromStorage, createContractPayload, saveSignedContractToStorage } from "../utils/contractUtils";

const ReadContractStep = ({ setActiveStep }: MailingStepProps) => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [pdf, setPdf] = useState<string | null>(null);
  const [isSecondDocument, setIsSecondDocument] = useState(false);
  const [showSecondDocumentButton, setShowSecondDocumentButton] = useState(false);
  const { getMailingSignedContract, finishMailingFlow, createContract } =
    useMailingOnBoardingService();

  const handleFinishMailingFlow = () => {
    setIsLoading(true);
    
    // Se é o segundo documento, chamar o /send endpoint
    if (isSecondDocument) {
      console.log('ReadContractStep - Finalizando com segundo documento, chamando /send');
      finishMailingFlow(id)
        .then(() => {
          // Limpar todos os dados do localStorage após envio
          localStorage.removeItem('signedContractDocument');
          localStorage.removeItem('secondSignedContractDocument');
          localStorage.removeItem('secondContractDocument');
          localStorage.removeItem('contractDocument');
          localStorage.removeItem('mailingData');
          localStorage.removeItem('userData');
          
          setActiveStep(EMailingStepsEnum.SentConfirmation);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Se não é segundo documento, verificar se precisa de segundo documento
      try {
        const { userData } = getContractDataFromStorage();
        if (needsSecondDocument(userData)) {
          console.log('ReadContractStep - Precisa de segundo documento, não pode finalizar ainda');
          // Se precisa de segundo documento, não deve chegar aqui pois o botão não deveria aparecer
          // Mas por segurança, não fazer nada
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error('ReadContractStep - Erro ao verificar segundo documento:', error);
      }
      
      // CORREÇÃO: Se não precisa de segundo documento, TAMBÉM deve chamar /send
      console.log('ReadContractStep - Finalizando com apenas um documento, chamando /send');
      finishMailingFlow(id)
        .then(() => {
          // Limpar todos os dados do localStorage após envio
          localStorage.removeItem('signedContractDocument');
          localStorage.removeItem('secondSignedContractDocument');
          localStorage.removeItem('secondContractDocument');
          localStorage.removeItem('contractDocument');
          localStorage.removeItem('mailingData');
          localStorage.removeItem('userData');
          
          setActiveStep(EMailingStepsEnum.SentConfirmation);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleCreateSecondDocument = async () => {
    setIsLoading(true);
    try {
      // Recuperar dados do localStorage
      const { mailingData, userData } = getContractDataFromStorage();
      
      // Criar payload para o segundo documento (order: 2)
      const contractPayload = createContractPayload(mailingData, userData, id || "", "", 2);
      
      console.log('ReadContractStep - Criando segundo documento...');
      const contractResponse = await createContract(contractPayload);
      
      // Converter resposta para base64 se necessário
      let contractBase64;
      if (contractResponse instanceof ArrayBuffer) {
        const bytes = new Uint8Array(contractResponse);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        contractBase64 = btoa(binary);
      } else if (typeof contractResponse === 'string') {
        contractBase64 = contractResponse;
      } else {
        contractBase64 = contractResponse;
      }
      
      // Salvar segundo documento no localStorage
      localStorage.setItem('secondContractDocument', contractBase64);
      
      // Navegar para a tela de leitura do segundo documento
      setActiveStep(EMailingStepsEnum.ContractReading);
      
    } catch (error) {
      console.error('ReadContractStep - Erro ao criar segundo documento:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    
    console.log('ReadContractStep - Carregando contrato ASSINADO...');
    
    // PRIORIDADE 1: Segundo contrato assinado (finalizado)
    const secondSignedContract = localStorage.getItem('secondSignedContractDocument');
    if (secondSignedContract) {
      try {
        let cleanBase64 = secondSignedContract;
        if (cleanBase64.includes(',')) {
          cleanBase64 = cleanBase64.split(',')[1];
        }
        cleanBase64 = cleanBase64.replace(/[\r\n\s]/g, '');
        const dataUrl = `data:application/pdf;base64,${cleanBase64}`;
        
        console.log('ReadContractStep - Mostrando segundo contrato ASSINADO');
        setPdf(dataUrl);
        setIsSecondDocument(true);
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('ReadContractStep - Erro ao processar segundo contrato assinado:', error);
      }
    }
    
    // PRIORIDADE 2: Primeiro contrato assinado
    const signedContract = localStorage.getItem('signedContractDocument');
    if (signedContract) {
      try {
        let cleanBase64 = signedContract;
        if (cleanBase64.includes(',')) {
          cleanBase64 = cleanBase64.split(',')[1];
        }
        cleanBase64 = cleanBase64.replace(/[\r\n\s]/g, '');
        const dataUrl = `data:application/pdf;base64,${cleanBase64}`;
        
        console.log('ReadContractStep - Mostrando primeiro contrato ASSINADO');
        setPdf(dataUrl);
        
        // Verificar se precisa de segundo documento
        try {
          const { userData } = getContractDataFromStorage();
          const needsSecond = needsSecondDocument(userData);
          console.log('ReadContractStep - needsSecondDocument:', needsSecond);
          console.log('ReadContractStep - userData:', userData);
          
          if (needsSecond) {
            console.log('ReadContractStep - Precisa de segundo documento, mostrando botão');
            setShowSecondDocumentButton(true);
          } else {
            console.log('ReadContractStep - Não precisa de segundo documento, mostrando botão Concluir');
            // NÃO remover dados do localStorage aqui - eles serão removidos apenas após o /send
          }
        } catch (error) {
          console.error('ReadContractStep - Erro ao verificar segundo documento:', error);
        }
        
        setIsLoading(false);
        return;
      } catch (error) {
        console.error('ReadContractStep - Erro ao processar primeiro contrato assinado:', error);
      }
    }
    
    // PRIORIDADE 3: Fallback para backend
    console.log('ReadContractStep - Buscando contrato assinado do backend...');
    getMailingSignedContract(id)
      .then((response) => {
        setPdf(response);
      })
      .catch((error) => {
        console.error('ReadContractStep - Erro ao buscar do backend:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  return (
    <Stack height={pdf ? "calc(100% - 48px)" : "auto"}>
      <ContainerContent id="mailing-contract" centered>
        {pdf ? (
          <PdfViewer pdfUrl={pdf} />
        ) : (
          // <div style={{ height: "100%", width: "100%" }}>
          //   {/* <iframe
          //     src={pdf}
          //     style={{ width: "100%", height: "100%", border: "none" }}
          //     title="PDF"
          //   ></iframe> */}
          // </div>
          <Stack
            width="100%"
            height="300px"
            alignItems="center"
            justifyContent="center"
          >
            <StyledCircularProgress size={40} />
          </Stack>
        )}
      </ContainerContent>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="center"
        gap="24px"
        mt="8px"
      >
        
        {/* Mostrar botão para criar segundo documento se necessário */}
        {showSecondDocumentButton && !isSecondDocument && (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            disabled={isLoading}
            sx={{ paddingInline: "24px" }}
            startIcon={
              isLoading ? <StyledCircularProgress size={16} /> : undefined
            }
            onClick={handleCreateSecondDocument}
          >
            Assinar formulário adicional
          </Button>
        )}
        
        {/* Botão Concluir - só aparece se não precisa de segundo documento OU se já assinou o segundo */}
        {(!showSecondDocumentButton || isSecondDocument) && (
          <Button
            color="primary"
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{ paddingInline: "24px" }}
            startIcon={
              isLoading ? <StyledCircularProgress size={16} /> : undefined
            }
            onClick={handleFinishMailingFlow}
          >
            Concluir
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default ReadContractStep;
